import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import '../styles/pages/_enterprise-consolidation.scss';
import { API_CONFIG } from '../config/api';
import SelectDropdown from '../components/SelectDropdown';

interface HierarchyInfo {
  enterpriseId: string;
  isParent: boolean;
  isSub: boolean;
  parentEnterpriseId: string | null;
  hierarchyConfig: {
    relationshipType: 'standalone' | 'parent' | 'sub';
    hierarchyLevel: 0 | 1;
    billingModel: string;
    dateLinked: string | null;
    linkedBy: string | null;
  };
}

interface SubEnterprise {
  id: string;
  name: string;
  email: string;
  dateLinked: string;
  linkedBy: string;
  totalUsers: number;
}

interface LinkFormData {
  subEnterpriseEmail: string;
}

interface CreateFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type TabType = 'overview' | 'link-enterprise' | 'create-enterprise';

const EnterpriseConsolidationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [hierarchyInfo, setHierarchyInfo] = useState<HierarchyInfo | null>(null);
  const [subEnterprises, setSubEnterprises] = useState<SubEnterprise[]>([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'created' | 'linked'>('all');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form states
  const [linkFormData, setLinkFormData] = useState<LinkFormData>({
    subEnterpriseEmail: ''
  });

  const [createFormData, setCreateFormData] = useState<CreateFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Fetch hierarchy info on component mount and when page becomes visible
  useEffect(() => {
    if (token) {
      fetchHierarchyInfo();
    }
  }, [token]);

  const fetchHierarchyInfo = async () => {
    try {
      setLoading(true);
      let url = `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/info`;
      if (user?.role === 'Admin') {
        url += `?enterpriseId=${user.id}`;
      }
      // Add cache-busting query parameter
      url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
      
      const response = await fetch(
        url,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch hierarchy information: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error(data.message || 'Invalid response from server');
      }
      
      setHierarchyInfo(data.data);

      // If parent, fetch sub-enterprises
      if (data.data.isParent) {
        fetchSubEnterprises(data.data.enterpriseId);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      toast.error(errorMsg);
      console.error('Error fetching hierarchy info:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubEnterprises = async (parentId?: string) => {
    try {
      let url = `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/sub-enterprises`;
      if (user?.role === 'Admin' && parentId) {
        url += `?parentId=${parentId}`;
      }
      // Add cache-busting query parameter
      url += (url.includes('?') ? '&' : '?') + `t=${Date.now()}`;
      
      const response = await fetch(
        url,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sub-enterprises: ${response.status}`);
      }

      const data = await response.json();
      
      // Set the sub-enterprises data
      const subs = data.data.subEnterprises || data.data || [];
      setSubEnterprises(subs);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch sub-enterprises';
      toast.error('Failed to refresh branch list: ' + errorMsg);
    }
  };

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hierarchyInfo?.enterpriseId) {
      toast.error('Error: Parent enterprise ID not found. Please refresh the page.');
      return;
    }

    try {
      setLinkLoading(true);

      const payload = {
        parentEnterpriseId: hierarchyInfo.enterpriseId,
        subEnterpriseEmail: linkFormData.subEnterpriseEmail
      };

      console.log('[HandleLinkSubmit] Sending payload:', payload);

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/link-enterprise`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json();
      console.log('[HandleLinkSubmit] Response:', data);

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to link enterprise');
      }

      toast.success(`Successfully linked "${linkFormData.subEnterpriseEmail}" as a sub-enterprise!`);
      setLinkFormData({ subEnterpriseEmail: '' });

      // Refresh data
      setTimeout(() => {
        fetchHierarchyInfo();
      }, 1500);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      toast.error(errorMsg);
      console.error('Link Enterprise Error:', err);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (createFormData.password !== createFormData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (createFormData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!hierarchyInfo?.enterpriseId) {
      toast.error('Error: Parent enterprise ID not found. Please refresh the page.');
      return;
    }

    try {
      setCreateLoading(true);

      const payload = {
        parentEnterpriseId: hierarchyInfo.enterpriseId,
        name: createFormData.name,
        email: createFormData.email,
        password: createFormData.password
      };

      console.log('[HandleCreateSubmit] Sending payload:', payload);

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/create-enterprise`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await response.json();
      console.log('[HandleCreateSubmit] Response:', data);

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to create sub-enterprise');
      }

      toast.success(`Successfully created "${createFormData.name}" as a sub-enterprise! Ready for user imports.`);
      setCreateFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
      });

      // Refresh data
      setTimeout(() => {
        fetchHierarchyInfo();
      }, 1500);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      toast.error(errorMsg);
      console.error('Create Enterprise Error:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleLinkInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLinkFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="enterprise-consolidation">
        <div className="enterprise-consolidation__loading">
          <div className="enterprise-consolidation__spinner"></div>
          <p>Loading enterprise information...</p>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === 'Admin';
  const isParentEnterprise = hierarchyInfo?.isParent || isAdmin;
  const isSubEnterprise = hierarchyInfo?.isSub && !isAdmin;

  // Filter sub-enterprises based on search and filter type
  const filteredSubEnterprises = subEnterprises.filter(sub => {
    const matchesSearch = sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sub.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || sub.linkedBy === (filterType === 'created' ? 'creation' : 'linking');
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="enterprise-consolidation">
      <div className="enterprise-consolidation__container">
        {/* Header */}
        <header className="enterprise-consolidation__header">
          <nav className="enterprise-consolidation__breadcrumb">
            <span>Dashboard</span>
            <span className="enterprise-consolidation__breadcrumb-sep">&gt;</span>
            <span className="enterprise-consolidation__breadcrumb-current">Enterprise Management</span>
          </nav>
          <div className="enterprise-consolidation__header-content">
            <h1 className="enterprise-consolidation__title">Enterprise Management</h1>
            <p className="enterprise-consolidation__subtitle">Manage corporate hierarchy and branch connections.</p>
          </div>
        </header>

        {/* Current Status Card */}
        {hierarchyInfo && user && (
          <section className="enterprise-consolidation__summary-card">
            <div className="enterprise-consolidation__summary-main">
              <div>
                <h2 className="enterprise-consolidation__summary-title">
                  {user.profile?.preferredName || `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || user.email}
                </h2>
                <div className="enterprise-consolidation__summary-badges">
                  <span className="enterprise-consolidation__summary-badge">{isParentEnterprise ? 'Parent Enterprise' : isSubEnterprise ? 'Branch' : 'Standalone'}</span>
                  <span className="enterprise-consolidation__summary-status-badge"><CheckCircle size={16} /> Active</span>
                </div>
                <div className="enterprise-consolidation__summary-location">
                  Enterprise Account • {user.email}
                </div>
              </div>
              <button
                className="enterprise-consolidation__summary-edit"
                onClick={() => {
                  if (user?.id) {
                    navigate(`/panel/enterprise/profile/${user.id}`);
                  }
                }}
              >
                Edit Profile
              </button>
            </div>
            <div className="enterprise-consolidation__summary-grid">
              <div>
                <label>ENTERPRISE ID</label>
                <div>{hierarchyInfo.enterpriseId}</div>
              </div>
              <div>
                <label>EMAIL</label>
                <div>{user.email}</div>
              </div>
              <div>
                <label>LINKED DATE</label>
                <div>{hierarchyInfo.hierarchyConfig.dateLinked ? new Date(hierarchyInfo.hierarchyConfig.dateLinked).toLocaleDateString() : '--'}</div>
              </div>
              <div>
                <label>TOTAL BRANCHES</label>
                <div>{subEnterprises.length} Sub-unit{subEnterprises.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          </section>
        )}

        {/* Tabs Navigation */}
        {isParentEnterprise && (
          <div className="enterprise-consolidation__tabs enterprise-consolidation__tabs--modern">
            <button
              className={`enterprise-consolidation__tab ${activeTab === 'overview' ? 'enterprise-consolidation__tab--active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <span className="enterprise-consolidation__tab-icon" role="img" aria-label="Overview">📋</span> Overview
            </button>
            <button
              className={`enterprise-consolidation__tab ${activeTab === 'link-enterprise' ? 'enterprise-consolidation__tab--active' : ''}`}
              onClick={() => setActiveTab('link-enterprise')}
            >
              <span className="enterprise-consolidation__tab-icon" role="img" aria-label="Link">🔗</span> Link Existing Branch
            </button>
            <button
              className={`enterprise-consolidation__tab ${activeTab === 'create-enterprise' ? 'enterprise-consolidation__tab--active' : ''}`}
              onClick={() => setActiveTab('create-enterprise')}
            >
              <span className="enterprise-consolidation__tab-icon" role="img" aria-label="Create">➕</span> Create New Branch
            </button>
          </div>
        )}

        {/* Tab Content */}
        <div className="enterprise-consolidation__content">
          {/* Overview Tab */}
          {activeTab === 'overview' && isParentEnterprise && (
            <div className="enterprise-consolidation__overview">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0 }}>Sub-Enterprises</h2>
              </div>

              {/* Search and Filter Bar */}
              <div className="enterprise-consolidation__search-filter-container">
                <div className="enterprise-consolidation__search-bar">
                  <svg className="enterprise-consolidation__search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="9" cy="9" r="8"></circle>
                    <path d="m14 14 4 4"></path>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    className="enterprise-consolidation__search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <SelectDropdown
                  value={filterType}
                  onChange={(value) => setFilterType(value as 'all' | 'created' | 'linked')}
                  options={[
                    { value: 'all', label: 'All Sub-Enterprises' },
                    { value: 'created', label: 'Created' },
                    { value: 'linked', label: 'Linked' }
                  ]}
                  placeholder="Filter by type"
                  className="enterprise-consolidation__filter-select"
                  ariaLabel="Filter sub-enterprises by type"
                />
              </div>

              {subEnterprises.length === 0 ? (
                <div className="enterprise-consolidation__empty-state">
                  <div className="enterprise-consolidation__empty-icon">📊</div>
                  <h3>No sub-enterprises yet</h3>
                  <p>
                    Link existing branches or create new sub-enterprises to start consolidating data.
                  </p>
                  <div className="enterprise-consolidation__empty-actions">
                    <button
                      className="enterprise-consolidation__btn enterprise-consolidation__btn--primary"
                      onClick={() => setActiveTab('link-enterprise')}
                    >
                      Link Existing Branch
                    </button>
                    <button
                      className="enterprise-consolidation__btn enterprise-consolidation__btn--secondary"
                      onClick={() => setActiveTab('create-enterprise')}
                    >
                      Create New Branch
                    </button>
                  </div>
                </div>
              ) : filteredSubEnterprises.length === 0 ? (
                <div className="enterprise-consolidation__empty-state">
                  <div className="enterprise-consolidation__empty-icon">🔍</div>
                  <h3>No results found</h3>
                  <p>
                    No sub-enterprises match your search or filter criteria.
                  </p>
                </div>
              ) : (
                <div className="enterprise-consolidation__sub-list enterprise-consolidation__sub-list--modern">
                  {filteredSubEnterprises.map(sub => (
                    <div key={sub.id} className="enterprise-consolidation__sub-card">
                      <div className="enterprise-consolidation__sub-card-header">
                        <span className="enterprise-consolidation__sub-card-icon" role="img" aria-label="Branch">🏢</span>
                        <div>
                          <div className="enterprise-consolidation__sub-card-title">{sub.name}</div>
                          <div className="enterprise-consolidation__sub-card-email">{sub.name}</div>
                        </div>
                        <span className="enterprise-consolidation__sub-card-badge">{sub.linkedBy === 'creation' ? 'Created' : 'Linked'}</span>
                      </div>
                      <div className="enterprise-consolidation__sub-card-details">
                        <div>
                          <label>Total Users</label>
                          <div>{sub.totalUsers}</div>
                        </div>
                        <div>
                          <label>Linked</label>
                          <div>{new Date(sub.dateLinked).toLocaleDateString()}</div>
                        </div>
                        <button
                          className="enterprise-consolidation__sub-card-action"
                          onClick={() => navigate(`/panel/enterprise/branch/${sub.id}`)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                  {/* Add card for linking new branch */}
                  <div className="enterprise-consolidation__sub-card enterprise-consolidation__sub-card--add" onClick={() => setActiveTab('create-enterprise')}>
                    <span className="enterprise-consolidation__sub-card-add-icon">＋</span>
                    <div>Link New Branch</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Link Existing Enterprise Tab */}
          {activeTab === 'link-enterprise' && isParentEnterprise && (
            <div className="enterprise-consolidation__form-section">
              <div className="enterprise-consolidation__form-header">
                <h2>Link Existing Branch</h2>
                <p className="enterprise-consolidation__form-description">
                  Link an existing enterprise as a sub-branch. All existing data will be preserved.
                </p>
              </div>

              <form
                className="enterprise-consolidation__form"
                onSubmit={handleLinkSubmit}
              >
                <div className="enterprise-consolidation__form-group">
                  <label htmlFor="subEnterpriseEmail" className="enterprise-consolidation__label">
                    Branch Email Address <span className="enterprise-consolidation__required">*</span>
                  </label>
                  <input
                    id="subEnterpriseEmail"
                    type="email"
                    name="subEnterpriseEmail"
                    className="enterprise-consolidation__input"
                    placeholder="branch@company.com"
                    value={linkFormData.subEnterpriseEmail}
                    onChange={handleLinkInputChange}
                    required
                    disabled={linkLoading}
                  />
                  <small className="enterprise-consolidation__help-text">
                    Enter the email address of the enterprise account you want to link as a branch
                  </small>
                </div>

                <div className="enterprise-consolidation__info-box">
                  <h4>What happens when you link a branch?</h4>
                  <ul>
                    <li>All existing user data and assessments are preserved</li>
                    <li>The branch becomes visible under your enterprise</li>
                    <li>You can view all combined data across branches</li>
                    <li>Each branch maintains separate billing</li>
                    <li>Branch users can only see their own branch data</li>
                  </ul>
                </div>

                <div className="enterprise-consolidation__form-actions">
                  <button
                    type="submit"
                    className="enterprise-consolidation__btn enterprise-consolidation__btn--primary"
                    disabled={linkLoading}
                  >
                    {linkLoading ? 'Linking...' : 'Link Branch'}
                  </button>
                  <button
                    type="button"
                    className="enterprise-consolidation__btn enterprise-consolidation__btn--secondary"
                    onClick={() => {
                      setLinkFormData({ subEnterpriseEmail: '' });
                    }}
                    disabled={linkLoading}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Create New Enterprise Tab */}
          {activeTab === 'create-enterprise' && isParentEnterprise && (
            <div className="enterprise-consolidation__form-section">
              <div className="enterprise-consolidation__form-header">
                <h2>Create New Branch</h2>
                <p className="enterprise-consolidation__form-description">
                  Create a new sub-enterprise for a new branch office. Ready for user imports immediately.
                </p>
              </div>

              <form
                className="enterprise-consolidation__form"
                onSubmit={handleCreateSubmit}
              >
                <div className="enterprise-consolidation__form-row">
                  <div className="enterprise-consolidation__form-group">
                    <label htmlFor="name" className="enterprise-consolidation__label">
                      Branch Name <span className="enterprise-consolidation__required">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      className="enterprise-consolidation__input"
                      placeholder="e.g., Branch A, New York Office"
                      value={createFormData.name}
                      onChange={handleCreateInputChange}
                      required
                      disabled={createLoading}
                    />
                  </div>

                  <div className="enterprise-consolidation__form-group">
                    <label htmlFor="email" className="enterprise-consolidation__label">
                      Enterprise Email <span className="enterprise-consolidation__required">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      className="enterprise-consolidation__input"
                      placeholder="branch@company.com"
                      value={createFormData.email}
                      onChange={handleCreateInputChange}
                      required
                      disabled={createLoading}
                    />
                  </div>
                </div>



                <div className="enterprise-consolidation__form-row">
                  <div className="enterprise-consolidation__form-group">
                    <label htmlFor="password" className="enterprise-consolidation__label">
                      Password <span className="enterprise-consolidation__required">*</span>
                    </label>
                    <div className="enterprise-consolidation__password-wrapper">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        className="enterprise-consolidation__input"
                        placeholder="Min 8 characters"
                        value={createFormData.password}
                        onChange={handleCreateInputChange}
                        required
                        disabled={createLoading}
                      />
                      <button
                        type="button"
                        className="enterprise-consolidation__password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={createLoading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <small className="enterprise-consolidation__help-text">
                      Minimum 8 characters
                    </small>
                  </div>

                  <div className="enterprise-consolidation__form-group">
                    <label htmlFor="confirmPassword" className="enterprise-consolidation__label">
                      Confirm Password <span className="enterprise-consolidation__required">*</span>
                    </label>
                    <div className="enterprise-consolidation__password-wrapper">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        className="enterprise-consolidation__input"
                        placeholder="Confirm password"
                        value={createFormData.confirmPassword}
                        onChange={handleCreateInputChange}
                        required
                        disabled={createLoading}
                      />
                      <button
                        type="button"
                        className="enterprise-consolidation__password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={createLoading}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="enterprise-consolidation__info-box">
                  <h4>What happens when you create a branch?</h4>
                  <ul>
                    <li>New branch account is created instantly</li>
                    <li>Branch is ready to import users immediately</li>
                    <li>You can view all data across all branches</li>
                    <li>Branch admin can manage their own users</li>
                    <li>Separate billing for each branch</li>
                  </ul>
                </div>

                <div className="enterprise-consolidation__form-actions">
                  <button
                    type="submit"
                    className="enterprise-consolidation__btn enterprise-consolidation__btn--primary"
                    disabled={createLoading}
                  >
                    {createLoading ? 'Creating...' : 'Create Branch'}
                  </button>
                  <button
                    type="button"
                    className="enterprise-consolidation__btn enterprise-consolidation__btn--secondary"
                    onClick={() => {
                      setCreateFormData({
                        name: '',
                        email: '',
                        password: '',
                        confirmPassword: ''
                      });
                    }}
                    disabled={createLoading}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Not Parent Enterprise Message */}
          {!isParentEnterprise && (
            <div className="enterprise-consolidation__empty-state">
              <div className="enterprise-consolidation__empty-icon">🔒</div>
              <h3>Consolidation Features Not Available</h3>
              <p>
                {isSubEnterprise
                  ? 'This feature is only available for parent enterprises. Contact your parent enterprise administrator.'
                  : 'To use consolidation features, your enterprise must be set up as a parent enterprise.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseConsolidationPage;
